/**
 * OnboardingDisclaimer.
 *
 * First-login modal gate mounted in AppLayout. Blocks interaction until
 * the user accepts the platform disclaimer. Vocabulary-aware copy uses the
 * tenant's vertical name and people labels.
 */
import { ShieldCheck } from "lucide-react";

import {
  useAcceptDisclaimerMutation,
  useGetDisclaimerStatusQuery,
} from "@/features/auth/api/authApi";
import { useRoleAccess } from "@/features/auth/model/useRoleAccess";

import { useVerticalVocab } from "@/shared/lib/verticals";
import { Button } from "@/shared/ui/Button";

function roleWelcome(role: string, peoplePlural: string): string {
  switch (role) {
    case "OWNER":
    case "ADMIN":
      return `Manage your organization, track ${peoplePlural}, and run programs from one place.`;
    case "ACCOUNTANT":
      return `Review financial activity and support your organization's ${peoplePlural}.`;
    case "STAFF":
    case "MENTOR":
      return `Serve your ${peoplePlural}, log activity, and stay coordinated with your team.`;
    case "BOARD":
      return `Stay informed about your organization's impact and ${peoplePlural}.`;
    case "VOLUNTEER":
      return `Log your hours, view shifts, and support your organization's mission.`;
    default:
      return `Collaborate with your team and support your organization's ${peoplePlural}.`;
  }
}

/** First-login disclaimer modal - shown until the user accepts once. */
export function OnboardingDisclaimer() {
  const v = useVerticalVocab();
  const { role } = useRoleAccess();
  const { data, isLoading } = useGetDisclaimerStatusQuery();
  const [acceptDisclaimer, { isLoading: isAccepting }] = useAcceptDisclaimerMutation();

  if (isLoading || !data || data.accepted) {
    return null;
  }

  const peopleLabel = v.people.plural;

  return (
    <div
      className="tnn-overlay fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-disclaimer-title"
    >
      <div className="w-full max-w-lg rounded-xl bg-background p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck size={20} />
          </div>
          <div>
            <h2
              id="onboarding-disclaimer-title"
              className="text-xl font-semibold text-foreground"
            >
              Welcome to {v.name}
            </h2>
            <p className="text-sm text-muted-foreground">{v.tagline}</p>
          </div>
        </div>

        <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h3 className="mb-1 text-sm font-semibold text-foreground">
              Welcome
            </h3>
            <p>{roleWelcome(role, peopleLabel)}</p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-foreground">
              Data handling
            </h3>
            <p>
              Your organization's data is stored securely and is not shared with
              third parties. Access is limited to authorized team members at your
              organization.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-foreground">
              Responsible use
            </h3>
            <p>
              By continuing, you agree to use this platform ethically and in
              accordance with your organization's policies and applicable laws.
            </p>
          </section>
        </div>

        <Button
          variant="primary"
          className="mt-6 w-full"
          disabled={isAccepting}
          onClick={() => acceptDisclaimer()}
          data-testid="onboarding-disclaimer-accept"
        >
          I Agree - Get Started
        </Button>
      </div>
    </div>
  );
}
